import * as React from 'react';
import { useEffect, useMemo, useState } from "react";
import { Box, Button, Card, CardContent, CardMedia, Grid, TablePagination, Typography, TextField, InputAdornment, IconButton, Stack, Snackbar, Alert } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import GoogleAd from "../../Components/GoogleAd";
import { AD_CONFIG } from "../../config/ads";
import { toggleLikedAlbum, getLikedAlbums } from "../../utils/likeStorage";
import { useAlbums, useLikeAlbum } from "../../hooks/useAlbums";
import Seo from "../../Components/Seo";
import ResourceLearningSummary from "../../Components/ResourceLearningSummary";


const ShortVideos = () => {
    const { data, isLoading: loading } = useAlbums('all', 1, 1000);
    const albums = useMemo(() => data?.albums || [], [data?.albums]);
    const { mutateAsync: toggleLikeMutation } = useLikeAlbum();

    const [currentPage, setCurrentPage] = useState(0); // MUI uses 0-based index
    const [pageSize, setPageSize] = useState(50);
    const [searchTerm, setSearchTerm] = useState('');
    const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
    const [likedAlbums, setLikedAlbums] = useState<Set<number>>(getLikedAlbums());

    const filterShortVideo = useMemo(() => {
        if (!albums || albums.length === 0) return [];
        const filtered = albums
            .filter((album) => album.category === "ShortVideo")
            .filter((album) => {
                if (!searchTerm) return true;
                const searchLower = searchTerm.toLowerCase();
                return album.title.toLowerCase().includes(searchLower) ||
                    (album.description && album.description.toLowerCase().includes(searchLower));
            })
            .map((album) => ({
                id: album.id,
                title: album.title,
                description: album.description,
                coverImage: album.coverImage,
                uploadedBy: album.uploadedBy,
                category: album.category,
                like: album.like === null || album.like === undefined ? 0 : album.like,
            }))
            .sort((a, b) => a.title.localeCompare(b.title)); // Sort by title alphabetically

        // Apply client-side pagination
        const startIndex = currentPage * pageSize;
        const endIndex = startIndex + pageSize;
        return filtered.slice(startIndex, endIndex);
    }, [albums, currentPage, pageSize, searchTerm])

    const totalFilteredCount = useMemo(() => {
        if (!albums || albums.length === 0) return 0;
        return albums
            .filter((album) => album.category === "ShortVideo")
            .filter((album) => {
                if (!searchTerm) return true;
                const searchLower = searchTerm.toLowerCase();
                return album.title.toLowerCase().includes(searchLower) ||
                    (album.description && album.description.toLowerCase().includes(searchLower));
            }).length;
    }, [albums, searchTerm]);

    // Reset to first page when search term changes
    useEffect(() => {
        setCurrentPage(0);
    }, [searchTerm]);

    const handleChangePage = (_: unknown, newPage: number) => {
        setCurrentPage(newPage);
    };

    const handleLike = async (albumId: number, currentLikes: number) => {
        try {
            // Check if already liked
            const isLiked = likedAlbums.has(albumId);
            const action = isLiked ? 'unlike' : 'like';

            // Call API to toggle like via React Query mutation
            await toggleLikeMutation({ id: albumId, action });

            // Update localStorage
            toggleLikedAlbum(albumId);

            // Update local state for immediate UI update
            const newLikedAlbums = new Set(likedAlbums);
            if (isLiked) {
                newLikedAlbums.delete(albumId);
            } else {
                newLikedAlbums.add(albumId);
            }
            setLikedAlbums(newLikedAlbums);

            // Show success message
            setToast({
                open: true,
                message: action === 'like' ? 'Video liked!' : 'Video unliked!',
                severity: 'success'
            });
        } catch (error: any) {
            console.error('Error toggling like:', error);
            setToast({ open: true, message: error?.message || 'Failed to update like. Please try again.', severity: 'error' });
        }
    };

    const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPageSize(parseInt(e.target.value, 10));
        setCurrentPage(0);
    };

    const handleWatchVideo = (youtubeLink: string | undefined) => {
        if (youtubeLink) {
            // Ensure the link opens in a new tab
            window.open(youtubeLink, '_blank', 'noopener,noreferrer');
        }
    };

    const isValidYouTubeUrl = (url: string | undefined): boolean => {
        if (!url) return false;
        const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/;
        return youtubeRegex.test(url);
    };
    return (
        <Box sx={{ p: { xs: 1, sm: 2 } }}>
            <Seo
                title="Short Radiology Videos"
                description="Watch short educational radiology videos for high-yield signs, image interpretation patterns, and focused visual review."
                canonicalPath="/shortVideos"
            />
            {loading ? <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Loading...</Typography> : null}
            <Typography
                variant="h1"
                color="textSecondary"
                align="center"
                sx={{
                    fontSize: {
                        xs: '1.75rem',
                        sm: '2.5rem',
                        md: '3rem',
                    },
                    fontWeight: 700,
                    mb: { xs: 1, sm: 2 },
                    mx: 'auto',
                    textAlign: 'center',
                }}
            >
                Short Videos
            </Typography>
            <Typography
                variant="body1"
                color="text.secondary"
                align="center"
                sx={{
                    mb: { xs: 3, sm: 4 },
                    maxWidth: '900px',
                    mx: 'auto',
                    fontSize: { xs: '1rem', sm: '1.125rem' },
                    lineHeight: 1.8,
                    textAlign: 'center'
                }}
            >
                Watch concise and high-yield video tutorials focusing on essential radiology topics. These short videos are designed to explain rapid diagnostic clues, sign-based learning, and practical tips for image interpretation, making learning on-the-go effective and engaging.
            </Typography>
            <ResourceLearningSummary variant="videos" />
            <Box sx={{ mb: 3 }}>
                <TextField
                    fullWidth
                    placeholder="Search videos by title or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                        },
                    }}
                />
            </Box>
            {/* Ad after search bar - Only show if we have content */}
            {filterShortVideo.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    <GoogleAd
                        adSlot={AD_CONFIG.adUnits.albumPageTop}
                        format="horizontal"
                    />
                </Box>
            )}
            <Grid container spacing={2}  >
                {filterShortVideo.map((album, index) => {
                    return (
                        <Grid size={{ lg: 4, xs: 12, md: 4 }} key={album.id} display="flex">
                            <Card sx={{ borderRadius: 3, width: '100%', display: 'flex', flexDirection: 'column' }}>
                                <CardMedia
                                    component="img"
                                    image={album.coverImage}
                                    alt={album.title}
                                    sx={{ height: { xs: 200, sm: 250, md: 300 }, objectFit: 'cover' }}
                                    loading="lazy"
                                />
                                <CardContent sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2 } }}>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: {
                                                xs: '1rem',
                                                sm: '1.25rem',
                                            },
                                            lineHeight: 1.3,
                                            mb: 1,
                                        }}
                                    >
                                        {album.title}
                                    </Typography>
                                </CardContent>
                                <Box sx={{ p: { xs: 1.5, sm: 2 }, pt: 0 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                        <Button
                                            variant="outlined"
                                            onClick={() => handleWatchVideo(album.description)}
                                            disabled={!isValidYouTubeUrl(album.description)}
                                            size="small"
                                            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                        >
                                            Watch Video
                                        </Button>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleLike(album.id, album.like || 0)}
                                                sx={{ color: likedAlbums.has(album.id) ? 'error.main' : 'action.disabled' }}
                                            >
                                                {likedAlbums.has(album.id) ? (
                                                    <FavoriteIcon fontSize="small" />
                                                ) : (
                                                    <FavoriteBorderIcon fontSize="small" />
                                                )}
                                            </IconButton>
                                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: '20px', textAlign: 'center' }}>
                                                {typeof album.like === 'number' ? album.like : 0}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </Box>
                            </Card>

                        </Grid>
                    )
                })}
            </Grid>

            {/* Ad before pagination */}
            {filterShortVideo.length > 0 && (
                <Box sx={{ mt: 3, mb: 2 }}>
                    <GoogleAd
                        adSlot={AD_CONFIG.adUnits.albumPageBottom}
                        format="horizontal"
                    />
                </Box>
            )}

            <TablePagination
                component="div"
                count={totalFilteredCount} // total filtered ShortVideo albums
                page={currentPage} // 0-based
                onPageChange={handleChangePage}
                rowsPerPage={pageSize}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />

            <Snackbar
                open={toast.open}
                autoHideDuration={3000}
                onClose={() => setToast({ ...toast, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setToast({ ...toast, open: false })} severity={toast.severity} sx={{ width: '100%' }}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ShortVideos;
